import type {
  ViewerDataAdapter,
  ViewerRequestContext,
} from '@ironview/interactive-viewer';

import {
  getAreaTags,
  getAvailablePhotos,
  getLayouts,
  getPhoto360,
  getPhotoMarkers,
  savePhotoMarkers,
} from '../api/layout-api';
import {
  createHotspot,
  deleteHotspot,
  getLayoutHotspots,
  getPhotoHotspots,
  updateHotspot,
} from '../api/hotspot-api';
import {
  attachPdfToLayout,
  copyPdfLayoutToLayout,
  detachPdfFromLayout,
} from '../api/layout-pdf-api';
import { listPDFs } from '@/features/pdf-editor/api';
import {
  createAreaTag as createAreaTagV3,
  deleteAreaTag as deleteAreaTagV3,
  fetchAreaTag,
  patchAreaTag,
} from '@/features/area-tags/api/area-tags-api';
import type {
  PhotoHotspot,
  PhotoHotspotsResponse,
} from '../types/types';
import {
  fromViewerLayoutMarker,
  toViewerAreaTag,
  toViewerLayout,
  toViewerLayoutHotspotSummary,
  toViewerLayoutMarker,
  toViewerPanoramaHotspot,
  toViewerPanoramaHotspots,
  toViewerPanoramaPhoto,
  toViewerPhoto,
} from './ironview360Adapter';
import { normalizeIronview360LayoutHotspot } from './ironview360HotspotNormalizer';

export const IRONVIEW_360_VIEWER_ADAPTER_ID = 'ironview-360';

export const createIronview360ViewerContext = (
  projectId: string | number,
  layoutType?: string,
): ViewerRequestContext => ({
  adapterId: IRONVIEW_360_VIEWER_ADAPTER_ID,
  projectId,
  layoutType,
});

const normalizePhotoHotspotResponse = (
  response: PhotoHotspotsResponse | readonly PhotoHotspot[],
): readonly PhotoHotspot[] =>
  'hotspots' in response ? response.hotspots ?? [] : response;

const numericId = (value: string | number, label: string): number => {
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) {
    throw new Error(`${label} must be numeric for the Ironview 360 API`);
  }
  return normalized;
};

const requirePdfAsset = (
  layout: Parameters<typeof toViewerLayout>[0],
) => {
  const normalized = toViewerLayout(layout);
  if (normalized.asset.kind !== 'pdf') {
    throw new Error(
      `Layout ${layout.id} did not return an attached PDF asset`,
    );
  }
  return normalized.asset;
};

export const ironview360ViewerAdapter: ViewerDataAdapter = {
  id: IRONVIEW_360_VIEWER_ADAPTER_ID,
  queries: {
    listLayouts: async ({ context }) => {
      const layouts = await getLayouts(
        String(context.projectId),
        context.layoutType,
      );
      return layouts.map((layout) => toViewerLayout(layout));
    },
    getMarkers: async ({ layoutId }) => {
      const markers = await getPhotoMarkers(String(layoutId));
      return markers.map(toViewerLayoutMarker);
    },
    getAreaTags: async ({ context, layoutId }) => {
      const areaTags = await getAreaTags(
        String(layoutId),
        String(context.projectId),
      );
      return areaTags.map(toViewerAreaTag);
    },
    getAvailablePhotos: async ({ layoutId }) => {
      const photos = await getAvailablePhotos(String(layoutId));
      return photos.map(toViewerPhoto);
    },
    getPanoramaPhoto: async ({ photoId }) => {
      const photo = await getPhoto360(Number(photoId));
      return toViewerPanoramaPhoto(photo);
    },
    getPhotoHotspots: async ({ photoId }) => {
      const response = await getPhotoHotspots(Number(photoId));
      return toViewerPanoramaHotspots(
        normalizePhotoHotspotResponse(response),
        photoId,
      );
    },
    getLayoutHotspots: async ({ layoutId }) => {
      const rawHotspots = await getLayoutHotspots(String(layoutId));
      return rawHotspots
        .map(normalizeIronview360LayoutHotspot)
        .filter((hotspot) => hotspot != null)
        .map((hotspot) =>
          toViewerLayoutHotspotSummary(hotspot, layoutId),
        );
    },
  },
  commands: {
    saveMarkers: async ({ layoutId, markers }) => {
      const legacyMarkers = markers.map(fromViewerLayoutMarker);
      const firstMarker = legacyMarkers[0];
      await savePhotoMarkers(String(layoutId), legacyMarkers, {
        width: firstMarker?.width ?? 30,
        height: firstMarker?.height ?? 18,
        fontSize: firstMarker?.fontSize ?? 12,
        aspectRatio:
          firstMarker && firstMarker.height !== 0
            ? firstMarker.width / firstMarker.height
            : 5 / 3,
      });
    },
    createAreaTag: async ({
      context,
      layoutId,
      name,
      points,
      markerIds,
      photoIds,
    }) => {
      const response = await createAreaTagV3(
        {
          name,
          layout: String(layoutId),
          coordinates: points.map(([x, y]) => ({ x, y })),
          marker_ids: markerIds.map((id) => numericId(id, 'Marker ID')),
          photos: photoIds.map((id) => numericId(id, 'Photo ID')),
        },
        String(context.projectId),
      );
      if (response.data) return toViewerAreaTag(response.data);
      const areaTagId = response.area_tag_id;
      if (areaTagId == null) {
        throw new Error(response.message || 'Unit/location creation returned no ID');
      }
      const created = await fetchAreaTag(
        areaTagId,
        String(layoutId),
        String(context.projectId),
      );
      return toViewerAreaTag(created);
    },
    updateAreaTag: async ({
      context,
      layoutId,
      areaTagId,
      changes,
    }) => {
      const label = changes.label;
      const response = await patchAreaTag(
        numericId(areaTagId, 'Unit/location ID'),
        {
          name: changes.name,
          coordinates: changes.points?.map(([x, y]) => ({ x, y })),
          marker_ids: changes.markerIds?.map((id) =>
            numericId(id, 'Marker ID'),
          ),
          photo_ids: changes.photoIds?.map((id) =>
            numericId(id, 'Photo ID'),
          ),
          text_position:
            label?.position === undefined
              ? undefined
              : label.position
                ? [label.position[0], label.position[1]]
                : null,
          text_size: label?.size,
          text_rotation: label?.rotation,
        },
        String(layoutId),
        String(context.projectId),
      );
      if (response.data) return toViewerAreaTag(response.data);
      const updated = await fetchAreaTag(
        numericId(areaTagId, 'Unit/location ID'),
        String(layoutId),
        String(context.projectId),
      );
      return toViewerAreaTag(updated);
    },
    deleteAreaTag: async ({
      context,
      layoutId,
      areaTagId,
    }) => {
      await deleteAreaTagV3(
        numericId(areaTagId, 'Unit/location ID'),
        String(layoutId),
        String(context.projectId),
      );
    },
    createHotspot: async ({ photoId, position, title }) => {
      const response = await createHotspot(
        numericId(photoId, 'Photo ID'),
        position,
      );
      return toViewerPanoramaHotspot(
        {
          ...response,
          title: title ?? response.title,
          photo_id: response.photo_id ?? response.photo,
          created_by: response.created_by_name ?? null,
          objects: response.objects ?? [],
        },
        photoId,
      );
    },
    updateHotspot: async ({ context, hotspotId, changes }) => {
      const response = await updateHotspot(
        numericId(hotspotId, 'Hotspot ID'),
        changes,
        String(context.projectId),
      );
      return toViewerPanoramaHotspot({
        ...response,
        photo_id: response.photo_id ?? response.photo,
        created_by: response.created_by_name ?? null,
        objects: response.objects ?? [],
      });
    },
    deleteHotspot: async ({ context, hotspotId }) => {
      await deleteHotspot(
        numericId(hotspotId, 'Hotspot ID'),
        String(context.projectId),
      );
    },
  },
  pdf: {
    listAssets: async ({ context }) => {
      const pdfFiles = await listPDFs(
        numericId(context.projectId, 'Project ID'),
      );
      return pdfFiles.map((pdfFile) => ({
        id: pdfFile.id,
        originalFilename: pdfFile.original_filename,
        pageCount: pdfFile.page_count,
        contentType: 'application/pdf',
      }));
    },
    attach: async ({
      context,
      layoutId,
      source,
      pageNumber,
      cropRegion,
      flippedHorizontal,
      flippedVertical,
    }) => {
      const response = await attachPdfToLayout(
        String(layoutId),
        source.kind === 'upload'
          ? source.file
          : String(source.assetId),
        {
          pageNumber,
          cropRegion: cropRegion ? [...cropRegion] : cropRegion,
          flippedHorizontal,
          flippedVertical,
        },
        numericId(context.projectId, 'Project ID'),
      );
      return requirePdfAsset(response.layout);
    },
    copyConfiguration: async ({
      layoutId,
      sourceLayoutId,
      copyMarkers,
      flippedHorizontal,
      flippedVertical,
    }) => {
      const response = await copyPdfLayoutToLayout(
        String(layoutId),
        String(sourceLayoutId),
        copyMarkers,
        { flippedHorizontal, flippedVertical },
      );
      return requirePdfAsset(response.layout);
    },
    detach: async ({ layoutId }) => {
      await detachPdfFromLayout(String(layoutId));
    },
  },
};
